<template>
  <div>
    <AdvancedScrollListLoader v-if="$props.loading">
      <template v-slot:default="slotProps">
        <slot name="loader" v-bind="slotProps" />
      </template>
    </AdvancedScrollListLoader>

    <b-list-group v-else ref="listRef" flush>
      <slot name="header" />

      <div class="w-100 h-100 position-sticky top-0 d-flex flex-column">
        <template v-if="!!visibleItems.length">
          <AdvancedScrollListItem
            v-for="(item, index) in visibleItems"
            :key="`advanced-scroll-list-item-${index}`"
            :style="{ minHeight: `${$props.itemHeight}px` }"
          >
            <template v-slot:default>
              <slot name="item" v-bind="{ item }">
                {{ item }}
              </slot>
            </template>
          </AdvancedScrollListItem>
        </template>

        <b-list-group-item v-else class="py-2 px-0 text-center">
          <slot name="empty-message">No items found.</slot>
        </b-list-group-item>
      </div>

      <slot name="footer" />

      <div
        class="list-group-filler"
        :style="{ height: `${listFillerHeight}px` }"
      ></div>
    </b-list-group>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  watch,
  nextTick
} from '@vue/composition-api'
import { throttle } from 'lodash-es'

import AdvancedScrollListLoader from './AdvancedScrollListLoader.vue'
import AdvancedScrollListItem from './AdvancedScrollListItem.vue'

export default defineComponent({
  name: 'AdvancedScrollList',
  components: {
    AdvancedScrollListLoader,
    AdvancedScrollListItem
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    itemHeight: {
      type: Number,
      default: 34
    },
    loading: Boolean
  },
  setup(props) {
    // List
    const listRef = ref(null)

    const listFillerHeight = computed(() =>
      Math.ceil(props.itemHeight * props.items.length)
    )

    // Visible items
    const visibleItems = ref([])

    const calcVisibleItems = async () => {
      await nextTick()

      const { scrollTop, clientHeight } = listRef.value || {}

      const count = Math.ceil(clientHeight / props.itemHeight)

      const maxIndex = props.items.length - count

      let startIndex = Math.floor(scrollTop / props.itemHeight)

      if (startIndex >= maxIndex) {
        startIndex = maxIndex
      }

      startIndex = Math.max(0, startIndex)

      const children = new Array(count || 0)
        .fill(null)
        .map((_, index) => index + startIndex)

      visibleItems.value = Array.from(props.items).filter((_, index) =>
        children.includes(index)
      )
    }

    watch(listRef, async (newListRef) => {
      if (!newListRef) return

      await calcVisibleItems()

      newListRef.addEventListener(
        'scroll',
        throttle((evt) => {
          const { scrollTop, scrollHeight, clientHeight } = evt.target

          scrollPercentage.value =
            (scrollTop / (scrollHeight - clientHeight)) * 100
        }, 300)
      )
    })

    // Scrolling
    const scrollPercentage = ref(0)

    watch(scrollPercentage, calcVisibleItems)

    return {
      listRef,
      listFillerHeight,

      visibleItems
    }
  }
})
</script>

<style scoped>
.list-group {
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  display: block;
}
</style>
